import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Briefcase, ExternalLink, Search, Share2 } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { formatDisplayDate } from "@/lib/dateFormat";
import { toWordPreview } from "@/lib/textPreview";
import { buildShareUrl } from "@/lib/shareUrl";
import { shareContent } from "@/lib/shareContent";

const JobsList = () => {
  const navigate = useNavigate();
  const { jobs, jobCategories, jobLocations } = useData();
  const [category, setCategory] = useState("All");
  const [location, setLocation] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const categoryAndLocationFiltered = jobs.filter((j) => {
    const catMatch = category === "All" || j.category === category;
    const locMatch = location === "All" || j.location === location;
    return catMatch && locMatch;
  });
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filtered = !normalizedSearch
    ? categoryAndLocationFiltered
    : categoryAndLocationFiltered.filter((job) => {
        const searchHaystack = [
          job.title,
          job.company,
          job.location,
          job.category,
          job.type,
          job.salary,
          job.description,
          job.postedDate,
          formatDisplayDate(job.postedDate),
        ]
          .join(" ")
          .toLowerCase();

        return searchHaystack.includes(normalizedSearch);
      });

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedJobs = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const handleShareJob = async (job: (typeof jobs)[number]) => {
    const shareUrl = buildShareUrl("jobs", job.slug || job.id);
    const shareText = `${job.title} at ${job.company} - ${job.location}. Posted ${formatDisplayDate(job.postedDate)}. Apply: ${job.applyLink}`;

    setShareMessage("");
    const result = await shareContent({
      title: job.title,
      text: shareText,
      url: shareUrl,
      imageUrl: job.imageUrl,
    });

    if (result.status === "copied") {
      setShareMessage("Job link copied to clipboard.");
      return;
    }

    if (result.status === "unsupported") {
      setShareMessage("Sharing is not supported in this browser.");
      return;
    }

    if (result.status === "error") {
      setShareMessage("Unable to share this job right now.");
    }
  };

  const openJobDetails = (job: typeof jobs[number]) => {
    navigate(`/jobs/${job.slug || job.id}`);
  };

  return (
    <div className="py-10 md:py-14">
      <div className="container mx-auto px-4">
        <h1 className="section-title mb-2">Jobs & Learning</h1>
        <p className="text-muted-foreground mb-8 max-w-xl">Explore latest job openings and financial learning resources including IPO basics, stock market guides, and career development content.</p>

        <form onSubmit={onSearchSubmit} className="mb-8 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search jobs by title, company, category, location, date..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
          >
            <Search size={15} />
            Search
          </button>
        </form>

        <div className="flex flex-wrap gap-6 mb-8">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Category</label>
            <div className="flex gap-2 flex-wrap">
              {jobCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setCategory(cat);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${category === cat ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Location</label>
            <div className="flex gap-2 flex-wrap">
              {jobLocations.map((loc) => (
                <button
                  key={loc}
                  onClick={() => {
                    setLocation(loc);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${location === loc ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="columns-1 md:columns-2 gap-5 [column-fill:_balance]">
          {paginatedJobs.map((job, i) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="mb-5 break-inside-avoid"
            >
              <div
                className="finance-card p-5 cursor-pointer"
                role="link"
                tabIndex={0}
                onClick={() => openJobDetails(job)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openJobDetails(job);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <Link to={`/jobs/${job.slug || job.id}`} className="font-display font-semibold text-foreground hover:text-secondary transition-colors">
                    {job.title}
                  </Link>
                  <span className="badge-success shrink-0">{job.type}</span>
                </div>
                {(job.imageUrl || job.videoUrl) && (
                  <div className="mb-3 space-y-3">
                    {job.imageUrl && (
                      <img
                        src={job.imageUrl}
                        alt={job.title}
                        className="w-full h-40 object-cover rounded-lg border border-border"
                        onError={(e) => {
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    )}
                    {job.videoUrl && (
                      <YouTubeEmbed
                        url={job.videoUrl}
                        title={job.title}
                      />
                    )}
                  </div>
                )}
                <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1"><Briefcase size={14} /> {job.company}</p>
                <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1"><MapPin size={14} /> {job.location}</p>
                <p className="text-sm text-muted-foreground mb-4">{toWordPreview(job.description, 24)}</p>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{job.salary}</span>
                  <div className="flex items-center gap-3">
                    <Link to={`/jobs/${job.slug || job.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline">
                      Details
                    </Link>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleShareJob(job);
                      }}
                      className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    >
                      <Share2 size={14} />
                      Share
                    </button>
                    <a
                      href={job.applyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      className="inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline"
                    >
                      Apply Now <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">Posted: {formatDisplayDate(job.postedDate)}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {filtered.length === 0 && <div className="text-center py-12 text-muted-foreground">No jobs found matching your filters.</div>}
        {filtered.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
        {shareMessage && <div className="mt-2 text-sm text-muted-foreground">{shareMessage}</div>}

        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground leading-relaxed"><strong>Disclaimer:</strong> Job listings are collected from publicly available sources. We do not guarantee job availability, employer authenticity, or hiring outcomes.</p>
        </div>
      </div>
    </div>
  );
};

export default JobsList;
